import { BehaviorSubject, Subject } from "rxjs";
import { bufferCount, distinctUntilChanged, filter, map, pairwise, publishReplay, refCount, scan, share, startWith, switchMap, tap, withLatestFrom, } from "rxjs/operators";
import { FrameGenerator } from "./FrameGenerator";
import { StateContext } from "./StateContext";
import { SubscriptionHolder } from "../utils/SubscriptionHolder";
export class StateService {
    constructor() {
        this._appendNode$ = new Subject();
        this._subscriptions = new SubscriptionHolder();
        const subs = this._subscriptions;
        this._start$ = new Subject();
        this._frame$ = new Subject();
        this._fpsSampleRate = 30;
        this._contextOperation$ = new BehaviorSubject((context) => {
            return context;
        });
        this._context$ = this._contextOperation$.pipe(scan((context, operation) => {
            return operation(context);
        }, new StateContext()), publishReplay(1), refCount());
        this._state$ = this._context$.pipe(map((context) => {
            return context.state;
        }), distinctUntilChanged(), publishReplay(1), refCount());
        this._fps$ = this._start$.pipe(switchMap(() => {
            return this._frame$.pipe(bufferCount(1, this._fpsSampleRate), map(() => {
                return new Date().getTime();
            }), pairwise(), map((times) => {
                return Math.max(20, (1000 * this._fpsSampleRate) / (times[1] - times[0]));
            }), startWith(60));
        }), share());
        this._currentState$ = this._frame$.pipe(withLatestFrom(this._fps$, this._context$, (frameId, fps, context) => {
            return [frameId, fps, context];
        }), filter((fc) => {
            return fc[2].currentNode != null;
        }), tap((fc) => {
            fc[2].update(fc[1]);
        }), map((fc) => {
            return { fps: fc[1], id: fc[0], state: fc[2] };
        }), share());
        this._lastState$ = this._currentState$.pipe(publishReplay(1), refCount());
        const nodeChanged$ = this._currentState$.pipe(distinctUntilChanged(undefined, (f) => {
            return f.state.currentNode.key;
        }), publishReplay(1), refCount());
        const nodeChangedSubject$ = new Subject();
        subs.push(nodeChanged$.subscribe(nodeChangedSubject$));
        this._currentKey$ = new BehaviorSubject(null);
        subs.push(nodeChangedSubject$
            .pipe(map((f) => {
            return f.state.currentNode.key;
        }))
            .subscribe(this._currentKey$));
        this._currentNode$ = nodeChangedSubject$.pipe(map((f) => {
            return f.state.currentNode;
        }), publishReplay(1), refCount());
        this._reference$ = nodeChangedSubject$.pipe(map((f) => {
            return f.state.reference;
        }), distinctUntilChanged((r1, r2) => {
            return r1.lat === r2.lat && r1.lon === r2.lon;
        }, (reference) => {
            return { lat: reference.lat, lon: reference.lon };
        }), publishReplay(1), refCount());
        this._currentNodeExternal$ = nodeChanged$.pipe(map((f) => {
            return f.state.currentNode;
        }), publishReplay(1), refCount());
        subs.push(this._appendNode$
            .pipe(map((node) => {
            return (context) => {
                context.append([node]);
                return context;
            };
        }))
            .subscribe(this._contextOperation$));
        this._inMotionOperation$ = new Subject();
        subs.push(nodeChanged$
            .pipe(map(() => {
            return true;
        }))
            .subscribe(this._inMotionOperation$));
        this._inMotion$ = this._inMotionOperation$.pipe(distinctUntilChanged(), publishReplay(1), refCount());
        this._inTranslationOperation$ = new Subject();
        subs.push(nodeChanged$
            .pipe(map(() => {
            return true;
        }))
            .subscribe(this._inTranslationOperation$));
        this._inTranslation$ = this._inTranslationOperation$.pipe(distinctUntilChanged(), publishReplay(1), refCount());
        subs.push(this._state$.subscribe(() => {
            /*noop*/
        }));
        subs.push(this._currentNode$.subscribe(() => {
            /*noop*/
        }));
        subs.push(this._reference$.subscribe(() => {
            /*noop*/
        }));
        subs.push(this._currentNodeExternal$.subscribe(() => {
            /*noop*/
        }));
        subs.push(this._lastState$.subscribe(() => {
            /*noop*/
        }));
        subs.push(this._inMotion$.subscribe(() => {
            /*noop*/
        }));
        subs.push(this._inTranslation$.subscribe(() => {
            /*noop*/
        }));
        this._frameId = null;
        this._frameGenerator = new FrameGenerator(window);
    }
    get currentState$() {
        return this._currentState$;
    }
    get currentNode$() {
        return this._currentNode$;
    }
    get currentKey$() {
        return this._currentKey$;
    }
    get currentNodeExternal$() {
        return this._currentNodeExternal$;
    }
    get state$() {
        return this._state$;
    }
    get reference$() {
        return this._reference$;
    }
    get inMotion$() {
        return this._inMotion$;
    }
    get inTranslation$() {
        return this._inTranslation$;
    }
    get appendNode$() {
        return this._appendNode$;
    }
    dispose() {
        this.stop();
        this._subscriptions.unsubscribe();
    }
    appendNodes(nodes) {
        this._invokeContextOperation((context) => {
            context.append(nodes);
        });
    }
    prependNodes(nodes) {
        this._invokeContextOperation((context) => {
            context.prepend(nodes);
        });
    }
    removeNodes(n) {
        this._invokeContextOperation((context) => {
            context.remove(n);
        });
    }
    clearNodes() {
        this._invokeContextOperation((context) => {
            context.clear();
        });
    }
    clearPriorNodes() {
        this._invokeContextOperation((context) => {
            context.clearPrior();
        });
    }
    cutNodes() {
        this._invokeContextOperation((context) => {
            context.cut();
        });
    }
    setNodes(nodes) {
        this._invokeContextOperation((context) => {
            context.set(nodes);
        });
    }
    setSpeed(speed) {
        this._invokeContextOperation((context) => {
            context.setSpeed(speed);
        });
    }
    start() {
        if (this._frameId == null) {
            this._start$.next(null);
            this._frameId = this._frameGenerator.requestAnimationFrame(this._frame.bind(this));
            this._frame$.next(this._frameId);
        }
    }
    stop() {
        if (this._frameId != null) {
            this._frameGenerator.cancelAnimationFrame(this._frameId);
            this._frameId = null;
        }
    }
    _invokeContextOperation(action) {
        this._contextOperation$.next((context) => {
            action(context);
            return context;
        });
    }
    _frame() {
        this._frameId = this._frameGenerator.requestAnimationFrame(this._frame.bind(this));
        this._frame$.next(this._frameId);
    }
}
//# sourceMappingURL=StateService.js.map